import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import { totalPagesFromCount } from "../../../shared/ui/Pagination/paginationModel";
import { messageFromError } from "../../../shared/api/drfErrors";
import useDebounce from "../../../hooks/useDebounce";
import { getPublicProfileHref } from "../../../utils/publicProfile";

import {
  fetchUsersDirectory,
  isRequestAbortError,
  USER_DIRECTORY_PAGE_SIZE,
} from "../api/usersDirectory";
import {
  buildDirectoryFetchParams,
  buildDirectorySearchParams,
  normalizeDirectoryQuery,
  readDirectorySearchParams,
} from "../lib/userDirectoryQuery";

const FALLBACK_AVATAR = "/images/no-profile-picture.png";
const SEARCH_DEBOUNCE_MS = 300;

function visibleDirectoryEmail(user) {
  if (!user || typeof user.email !== "string") return "";
  return user.email.trim();
}

function UserDirectoryCard({ user }) {
  const href = getPublicProfileHref(user);
  if (!href) return null;
  const email = visibleDirectoryEmail(user);
  const handle = typeof user.handle === "string" && user.handle
    ? `@${user.handle}`
    : "";
  const name = user.fullname || handle || "Користувач";
  const photo = user.user_image || FALLBACK_AVATAR;

  return (
    <Link to={href} className="feature-user-directory__card">
      <span className="feature-user-directory__avatar-wrap">
        <img
          src={photo}
          alt=""
          className="feature-user-directory__avatar"
        />
      </span>
      <span className="feature-user-directory__identity">
        <span className="feature-user-directory__name">{name}</span>
        {handle ? (
          <span className="feature-user-directory__handle">{handle}</span>
        ) : null}
        {email ? (
          <span className="feature-user-directory__email">{email}</span>
        ) : null}
      </span>
      <span className="feature-user-directory__open" aria-hidden="true">
        <i className="fa-solid fa-chevron-right" />
      </span>
    </Link>
  );
}

function UserDirectoryScreenContent() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlState = readDirectorySearchParams(searchParams);
  const urlQRef = useRef(urlState.q);
  urlQRef.current = urlState.q;
  const [draftQ, setDraftQ] = useState(urlState.q);
  const debouncedDraft = useDebounce(draftQ, SEARCH_DEBOUNCE_MS);
  const prevDebouncedDraftRef = useRef(debouncedDraft);

  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState("");
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState(0);
  const [reloadNonce, setReloadNonce] = useState(0);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setDraftQ(urlState.q);
  }, [urlState.q]);

  useEffect(() => {
    if (prevDebouncedDraftRef.current === debouncedDraft) return;
    prevDebouncedDraftRef.current = debouncedDraft;
    const settled = normalizeDirectoryQuery(debouncedDraft);
    if (settled === urlQRef.current) return;
    setSearchParams(buildDirectorySearchParams({ q: settled, page: 1 }));
  }, [debouncedDraft, setSearchParams]);

  useEffect(() => {
    const controller = new AbortController();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoadState("loading");
    setLoadError("");

    fetchUsersDirectory(
      buildDirectoryFetchParams({
        q: urlState.q,
        page: urlState.page,
        pageSize: USER_DIRECTORY_PAGE_SIZE,
      }),
      { signal: controller.signal },
    )
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        const rows = Array.isArray(data?.results) ? data.results : [];
        const nextCount = Number.isFinite(Number(data?.count))
          ? Number(data.count)
          : rows.length;
        setUsers(rows);
        setCount(nextCount);
        setLoadState("success");
      })
      .catch((error) => {
        if (controller.signal.aborted || isRequestAbortError(error)) return;
        if (requestId !== requestIdRef.current) return;
        setUsers([]);
        setCount(0);
        setLoadError(messageFromError(error));
        setLoadState("error");
      });

    return () => {
      controller.abort();
    };
  }, [urlState.q, urlState.page, reloadNonce]);

  const totalPages = totalPagesFromCount(count, USER_DIRECTORY_PAGE_SIZE);
  const currentPage = urlState.page > totalPages ? totalPages : urlState.page;

  const handlePageChange = (page) => {
    setSearchParams(buildDirectorySearchParams({ q: urlState.q, page }));
  };

  const handleClearSearch = () => {
    setDraftQ("");
    setSearchParams(buildDirectorySearchParams({ q: "", page: 1 }));
  };

  const handleRetry = () => {
    setReloadNonce((value) => value + 1);
  };

  const resultLabel = urlState.q
    ? `Знайдено: ${count}`
    : `Користувачів: ${count}`;

  return (
    <div className="feature-user-directory">
      <section className="clients-overview" aria-label="Каталог користувачів">
        <header className="clients-header">
          <div className="clients-header__text">
            <h1 className="clients-header__title">Користувачі</h1>
            <p className="clients-header__sub">
              Шукайте колег за ім&apos;ям або @нікнеймом. Приватний email
              не використовується для пошуку.
            </p>
          </div>
        </header>
      </section>

      <form
        className="feature-user-directory__search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          const settled = normalizeDirectoryQuery(draftQ);
          setSearchParams(buildDirectorySearchParams({ q: settled, page: 1 }));
        }}
      >
        <label className="feature-user-directory__search-label" htmlFor="user-directory-q">
          Пошук користувача
        </label>
        <div className="feature-user-directory__search-row">
          <input
            id="user-directory-q"
            className="ui-text-input feature-user-directory__search-input"
            type="search"
            name="q"
            autoComplete="off"
            placeholder="Ім'я, прізвище або @handle"
            value={draftQ}
            onChange={(event) => setDraftQ(event.target.value)}
          />
          {draftQ ? (
            <button
              type="button"
              className="action-btn feature-user-directory__clear"
              onClick={handleClearSearch}
            >
              Очистити
            </button>
          ) : null}
          <button type="submit" className="action-btn action-btn--accent">
            Шукати
          </button>
        </div>
      </form>

      {loadState === "success" ? (
        <p className="feature-user-directory__count" data-testid="user-directory-count">
          {resultLabel}
        </p>
      ) : null}

      {loadState === "loading" ? (
        <div className="feature-user-directory__state">
          <Spinner size="lg" label="Завантаження користувачів" />
        </div>
      ) : null}

      {loadState === "error" ? (
        <div className="feature-user-directory__state">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити користувачів"
            description={loadError || "Спробуйте ще раз."}
            size="md"
            primaryAction={
              <button type="button" className="action-btn action-btn--accent" onClick={handleRetry}>
                Повторити
              </button>
            }
          />
        </div>
      ) : null}

      {loadState === "success" && users.length === 0 && urlState.q ? (
        <EmptyState
          icon="fa-solid fa-user-slash"
          title="Нікого не знайдено"
          description="Змініть запит або очистіть пошук."
          size="md"
          primaryAction={
            <button type="button" className="action-btn" onClick={handleClearSearch}>
              Очистити пошук
            </button>
          }
        />
      ) : null}

      {loadState === "success" && users.length === 0 && !urlState.q ? (
        <EmptyState
          icon="fa-solid fa-users"
          title="Поки що немає користувачів"
          description="Каталог порожній."
          size="md"
        />
      ) : null}

      {loadState === "success" && users.length > 0 ? (
        <ul className="feature-user-directory__list">
          {users.map((user) => (
            <li key={user.public_id || user.id}>
              <UserDirectoryCard user={user} />
            </li>
          ))}
        </ul>
      ) : null}

      {loadState === "success" ? (
        <NumberedPagination
          currentPage={currentPage}
          totalPages={totalPages}
          hasPrevious={Boolean(currentPage > 1)}
          hasNext={Boolean(currentPage < totalPages)}
          onPageChange={handlePageChange}
          ariaLabel="Сторінки каталогу користувачів"
          disabled={false}
        />
      ) : null}
    </div>
  );
}

export default UserDirectoryScreenContent;
